import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Search, Filter, Grid3x3, PanelsTopLeft, X, ChevronLeft, ChevronRight, Camera } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';

const PhotoGallery = () => {
    const [photos, setPhotos] = useState([]);
    const [viewMode, setViewMode] = useState('grid');
    const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [displayCount, setDisplayCount] = useState(12);
    const lightboxRef = useRef(null);

    useEffect(() => {
        const fetchPhotos = async () => {
            try {
                const response = await fetch("https://pub-fc38ed5357e14707b9135b45fb3e4561.r2.dev/photos.json");
                if (!response.ok) throw new Error("Failed to fetch photos");
                const data = await response.json();
                setPhotos(data);
                setLoading(false);
            } catch (err) {
                console.error("Error fetching photos:", err);
                setError(err.message);
                setLoading(false);
            }
        };

        fetchPhotos();
    }, []);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (selectedPhotoIndex === null) return;
            switch(e.key) {
                case 'ArrowLeft': navigatePhotos('prev'); break;
                case 'ArrowRight': navigatePhotos('next'); break;
                case 'Escape': setSelectedPhotoIndex(null); break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedPhotoIndex]);

    const filteredPhotos = useMemo(() => {
        return photos.filter(photo => {
            const matchesCategory = selectedCategory === 'all' || photo.category === selectedCategory;
            const matchesSearch = !searchTerm ||
                photo.caption?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                photo.category?.toLowerCase().includes(searchTerm.toLowerCase());
            return matchesCategory && matchesSearch;
        });
    }, [photos, selectedCategory, searchTerm]);

    const categories = useMemo(() => {
        const cats = new Set(photos.map(photo => photo.category));
        return ['all', ...Array.from(cats)].sort();
    }, [photos]);

    const navigatePhotos = (direction) => {
        if (selectedPhotoIndex === null) return;
        const newIndex = direction === 'next'
            ? (selectedPhotoIndex + 1) % filteredPhotos.length
            : (selectedPhotoIndex - 1 + filteredPhotos.length) % filteredPhotos.length;
        setSelectedPhotoIndex(newIndex);
    };

    if (loading) {
        return (
            <div className="min-h-[400px] flex items-center justify-center">
                <Camera className="w-12 h-12 animate-pulse text-primary" />
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="destructive">
                <AlertDescription>Error loading gallery: {error}</AlertDescription>
            </Alert>
        );
    }

    return (
        <div className="space-y-8">
            <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
                {/* Gallery Controls */}
                <div className="flex flex-col md:flex-row gap-6">
                    <div className="relative flex-grow">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                        <input
                            type="text"
                            placeholder="Search photos..."
                            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="flex gap-4">
                        <div className="flex items-center gap-2">
                            <Filter className="text-gray-400 h-4 w-4" />
                            <select
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                                {categories.map(cat => (
                                    <option key={cat} value={cat}>
                                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <button
                            onClick={() => setViewMode(viewMode === 'grid' ? 'masonry' : 'grid')}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            {viewMode === 'grid'
                                ? <><PanelsTopLeft className="h-4 w-4" /> Masonry</>
                                : <><Grid3x3 className="h-4 w-4" /> Grid</>
                            }
                        </button>
                    </div>
                </div>

                {/* Stats Bar */}
                <div className="flex items-center justify-between text-sm text-gray-600 border-t pt-4">
          <span>
            Showing {Math.min(displayCount, filteredPhotos.length)} of {filteredPhotos.length} photos
          </span>
                    <span>{categories.length - 1} categories</span>
                </div>
            </div>

            {/* Photo Grid */}
            <div className={`gap-6 ${
                viewMode === 'grid'
                    ? 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
                    : 'columns-1 sm:columns-2 md:columns-3 lg:columns-4'
            }`}>
                {filteredPhotos.slice(0, displayCount).map((photo, index) => (
                    <div
                        key={photo.id}
                        className={`group relative cursor-pointer ${viewMode === 'grid' ? '' : 'mb-6 break-inside-avoid'}`}
                        onClick={() => setSelectedPhotoIndex(index)}
                    >
                        <div className="relative overflow-hidden rounded-lg shadow-lg">
                            <img
                                src={photo.thumbnail || photo.gallery || photo.url}
                                alt={photo.caption}
                                className="w-full h-auto transition-transform duration-300 group-hover:scale-105"
                                loading="lazy"
                                decoding="async"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                            <div className="absolute bottom-0 left-0 right-0 p-4 text-white transform translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                                <h3 className="text-lg font-medium">{photo.caption}</h3>
                                {photo.category && (
                                    <span className="text-sm opacity-75">{photo.category}</span>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Load More Button */}
            {filteredPhotos.length > displayCount && (
                <div className="text-center">
                    <button
                        onClick={() => setDisplayCount(prev => Math.min(prev + 12, filteredPhotos.length))}
                        className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-light transition-colors"
                    >
                        Load More Photos
                    </button>
                </div>
            )}

            {/* Lightbox */}
            {selectedPhotoIndex !== null && (
                <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center backdrop-blur-sm">
                    <div ref={lightboxRef} className="relative max-w-7xl mx-auto px-4">
                        <button
                            onClick={() => setSelectedPhotoIndex(null)}
                            className="absolute -top-16 right-4 text-white hover:text-gray-300 p-2 z-10"
                            aria-label="Close lightbox"
                        >
                            <X className="w-6 h-6" />
                        </button>

                        <button
                            onClick={() => navigatePhotos('prev')}
                            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 p-2"
                            aria-label="Previous photo"
                        >
                            <ChevronLeft className="w-8 h-8" />
                        </button>

                        <button
                            onClick={() => navigatePhotos('next')}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 p-2"
                            aria-label="Next photo"
                        >
                            <ChevronRight className="w-8 h-8" />
                        </button>

                        <img
                            src={filteredPhotos[selectedPhotoIndex].fullsize || filteredPhotos[selectedPhotoIndex].gallery || filteredPhotos[selectedPhotoIndex].url}
                            alt={filteredPhotos[selectedPhotoIndex].caption}
                            className="max-h-[85vh] w-auto mx-auto rounded-lg shadow-2xl"
                            loading="eager"
                        />

                        <div className="absolute bottom-4 left-0 right-0 text-center text-white bg-black/50 backdrop-blur-sm p-4 rounded-lg mx-4">
                            <h3 className="text-xl font-medium mb-1">
                                {filteredPhotos[selectedPhotoIndex].caption}
                            </h3>
                            {filteredPhotos[selectedPhotoIndex].category && (
                                <p className="text-sm opacity-75">
                                    {filteredPhotos[selectedPhotoIndex].category}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PhotoGallery;